import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  LayoutDashboard,
  Users,
  FileText,
  DollarSign,
  Settings,
  LogOut,
  UserCircle,
  CloudLightning,
  Menu,
  X
} from 'lucide-react';

export const DashboardLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, roles: ['admin', 'manager', 'staff', 'viewer'] },
    { name: 'Customers', href: '/customers', icon: Users, roles: ['admin', 'manager', 'staff', 'viewer'] },
    { name: 'Claims', href: '/claims', icon: FileText, roles: ['admin', 'manager', 'staff', 'viewer'] },
    { name: 'CAT Events', href: '/cat-events', icon: CloudLightning, roles: ['admin'] },
    { name: 'Users', href: '/users', icon: UserCircle, roles: ['admin'] },
  ];

  const filteredNavigation = navigation.filter(item => 
    item.roles.includes(user?.role)
  );

  const handleNavClick = () => {
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-background-sidebar text-foreground-inverse h-16 flex items-center justify-between px-4">
        <h1 className="text-lg font-bold font-sans tracking-tight">ClaimForge</h1>
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 rounded-md hover:bg-slate-800"
          data-testid="mobile-menu-toggle"
        >
          {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Desktop always visible, Mobile slides in */}
      <div 
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white text-slate-900 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:bg-background-sidebar lg:text-foreground-inverse ${
          isMobileMenuOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full'
        }`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-slate-200 lg:border-slate-800">
            <h1 className="text-xl font-bold font-sans tracking-tight">ClaimForge</h1>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="lg:hidden p-1 rounded hover:bg-slate-100 lg:hover:bg-slate-800"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            {filteredNavigation.map((item) => {
              const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
              const Icon = item.icon;
              
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={handleNavClick}
                  data-testid={`nav-${item.name.toLowerCase()}`}
                  className={`flex items-center px-4 py-3 text-sm font-medium rounded-md transition-all duration-200 ${
                    isActive
                      ? 'bg-slate-100 text-slate-900 lg:bg-slate-800 lg:text-white'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 lg:text-slate-300 lg:hover:bg-slate-800 lg:hover:text-white'
                  }`}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* User Info */}
          <div className="p-4 border-t border-slate-200 lg:border-slate-800 safe-bottom">
            <div className="flex items-center justify-between mb-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 lg:text-white truncate" data-testid="user-name">{user?.name}</p>
                <p className="text-xs text-slate-500 lg:text-slate-400 capitalize" data-testid="user-role">{user?.role}</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="flex items-center w-full px-4 py-2 text-sm font-medium text-slate-600 lg:text-slate-300 rounded-md hover:bg-slate-100 lg:hover:bg-slate-800 hover:text-slate-900 lg:hover:text-white transition-all duration-200"
            >
              <LogOut className="w-4 h-4 mr-3" />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64 pt-16 lg:pt-0">
        <main className="min-h-screen">
          {children}
        </main>
      </div>
    </div>
  );
};